import * as amplitude from "@amplitude/analytics-browser";
import classNames from "classnames";
import { memo, useContext } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { TeamContext } from "../../../context/team-context";
import { Flex } from "../../../flex";
import { Tooltip } from "../../../tooltip/tooltip";
import { NavIconSwap } from "../common/redo-nav-icon-swap";
import * as navigationCss from "../redo-navigation.module.css";
import { PrimaryTab } from "./redo-primary-nav";

export interface PrimaryNavigationTabProps {
  tab: PrimaryTab;
  manuallySelected?: boolean;
}

export const PrimaryNavigationTab = memo(function PrimaryNavigationTab({
  tab,
  manuallySelected,
}: PrimaryNavigationTabProps) {
  const { UnfilledIcon, FilledIcon, hasNotification, type } = tab;
  const location = useLocation();
  const team = useContext(TeamContext);

  const href = type === "link" ? tab.pathRoot : "";

  const match = useMatch({ path: `${href}/*`, caseSensitive: true });

  const isSelected = (!!href && !!match) || !!manuallySelected;

  const as = type === "link" ? "a" : "button";

  const isOnReturnDetailsPage =
    /\/order-management\/returns\/[^/]+\/[^/]+$/.test(location.pathname);

  return (
    <Tooltip darkMode placement="right" title={tab.friendlyName}>
      <Flex
        align="center"
        as={as}
        className={classNames(
          navigationCss.primaryTab,
          isSelected && navigationCss.selected,
        )}
        data-testid={`primary-nav-${tab.id}`}
        justify="center"
        linkProps={tab.type === "link" ? { to: tab.href } : undefined}
        onClick={() => {
          amplitude.track("nav/sidebar-item-clicked", {
            section: tab.id,
            item: tab.id,
          });
          if (tab.type === "button") {
            tab.onClick();
          }
        }}
        {...(tab.id === "help" &&
          tab.type === "button" &&
          !team?.settings.globalAI &&
          !isOnReturnDetailsPage && { "data-intercom-target": "" })}
      >
        {hasNotification && <span className={navigationCss.notificationDot} />}
        <NavIconSwap
          FilledIcon={FilledIcon}
          isSelected={isSelected}
          size={20}
          UnfilledIcon={UnfilledIcon}
        />
      </Flex>
    </Tooltip>
  );
});
